import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  ChevronDown,
  ChevronUp,
  Clock,
  Crown,
  HelpCircle,
  Mail,
  MessageSquare,
  Shield,
  Zap
} from 'lucide-react';
import LegalLayout from '../LegalLayout';

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  const accentColor = '#4f46e5';

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)'
      }}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left transition-colors"
        style={{ color: 'var(--text-primary)' }}
      >
        <span className="font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: accentColor }} />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
        )}
      </button>
      {isOpen && (
        <div
          className="px-6 pb-4"
          style={{ color: 'var(--text-secondary)' }}
        >
          {answer}
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const accentColor = '#4f46e5';
  const [openFAQ, setOpenFAQ] = useState(0);

  const faqs = [
    {
      icon: <BookOpen className="w-5 h-5" />,
      question: "How do I log a decision?",
      answer: (
        <div className="space-y-2">
          <p>A four-step form captures the whole call:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>What you are deciding, with a category (Career, Finance, Health, and more)</li>
            <li>Your reasoning — and a counter-thesis, because future you deserves the other side too</li>
            <li>Your expected outcome, a 0–100% confidence level, and alternatives considered</li>
            <li>When to review it: 30 days, 90 days, 6 months, a year, or a custom date</li>
          </ol>
        </div>
      )
    },
    {
      icon: <Clock className="w-5 h-5" />,
      question: "What happens at review time?",
      answer: (
        <div className="space-y-2">
          <p>When the review date arrives (with an optional local reminder), Verdict shows exactly what you predicted and how confident you were, side by side with what actually happened. Rate it — better than expected, as expected, or worse — and note what you would do differently.</p>
        </div>
      )
    },
    {
      icon: <Zap className="w-5 h-5" />,
      question: "What is the calibration score?",
      answer: (
        <div className="space-y-2">
          <p>Once you have reviewed enough decisions, Verdict compares your stated confidence against your actual outcomes. Consistently 95% sure about things that go sideways? That is exactly the pattern the calibration score reveals.</p>
        </div>
      )
    },
    {
      icon: <Shield className="w-5 h-5" />,
      question: "Is my decision journal private?",
      answer: (
        <div className="space-y-2">
          <p>Completely. Reasoning, predictions, and reviews are stored only on your device. Verdict has no account, no analytics, and makes no network requests.</p>
        </div>
      )
    },
    {
      icon: <Crown className="w-5 h-5" />,
      question: "What is Verdict Pro and how do I restore it?",
      answer: (
        <div className="space-y-2">
          <p>Verdict Pro is an optional upgrade available as a monthly or yearly subscription, or a one-time lifetime unlock. To restore a previous purchase:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open Settings inside Verdict</li>
            <li>Tap Restore Purchases</li>
            <li>Sign in with the Apple ID you used to purchase</li>
          </ol>
        </div>
      )
    }
  ];

  return (
    <LegalLayout app="Verdict" docType="Support" icon={HelpCircle}>
        {/* Contact Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <a
            href="mailto:support@feraltechlabs.co.in"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <Mail className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Email Support
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  support@feraltechlabs.co.in
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              We typically respond within 24-48 hours
            </p>
          </a>

          <a
            href="mailto:support@feraltechlabs.co.in?subject=Verdict%20Bug%20Report"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <MessageSquare className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Report a Bug
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Help us improve Verdict
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Include your iOS version and steps to reproduce
            </p>
          </a>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>

        {/* App Info */}
        <div
          className="rounded-xl p-6"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            App Information
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>App Name:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Verdict</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Developer:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Feral Techlabs</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Platform:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>iOS 17+</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Category:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Productivity</span>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link
            to="/apps/verdict/privacy"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Privacy Policy
          </Link>
          <span style={{ color: 'var(--text-tertiary)' }}>•</span>
          <Link
            to="/apps/verdict/terms"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Terms &amp; Conditions
          </Link>
        </div>
    </LegalLayout>
  );
};

export default Support;
